// Fase 59/F3 — multiplicação de dinheiro que bate com a do servidor.
//
// O backend calcula `quantidade × unitário` em BigDecimal e RECUSA um total que
// não confira. Calculando em ponto flutuante, os dois discordam em casos
// legítimos (0,5 × 2,01 = 1,00499…) e o operador leva um 422 que não tem como
// corrigir: o número que ele vê na tela é justamente o que o servidor rejeita.
//
// `Number.EPSILON` não resolve — é uma constante absoluta (~2,2e-16) e o erro
// do float cresce com a magnitude, então a correção só vale perto de 1.
//
// Aqui a conta é feita em INTEIROS: cada operando vira dígitos + nº de casas, o
// produto é exato, e o arredondamento acontece sobre os DÍGITOS, por texto —
// nenhuma divisão em ponto flutuante antes de arredondar.

// Decimal simples, sem expoente: "1e-7" não tem ponto e seria lido como
// inteiro, e "1e400" é infinito. O formulário nunca produz essas formas e o
// servidor as recusa — aqui elas viram `null` em vez de um total inventado.
const DECIMAL_SIMPLES = /^-?\d+(\.\d+)?$/;

const escalar = valor => {
  const texto = String(valor);
  const ponto = texto.indexOf('.');
  if (ponto === -1) return { digitos: Number(texto), casas: 0 };
  return {
    digitos: Number(texto.slice(0, ponto) + texto.slice(ponto + 1)),
    casas: texto.length - ponto - 1,
  };
};

// HALF-UP sobre os dígitos, igual ao `BigDecimal#round` do Ruby (que não usa
// arredondamento bancário).
const emCentavos = (digitos, casas) => {
  if (casas <= 2) return digitos * 10 ** (2 - casas);

  const texto = String(digitos).padStart(casas + 1, '0');
  const corte = texto.length - (casas - 2);
  const truncado = Number(texto.slice(0, corte));
  return Number(texto[corte]) >= 5 ? truncado + 1 : truncado;
};

/**
 * Multiplica dois valores decimais NÃO NEGATIVOS e arredonda para centavos.
 * Quantidade é sempre positiva e unitário nunca é negativo (o servidor recusa),
 * por isso não há tratamento de sinal.
 *
 * @param {number|string} quantidade
 * @param {number|string} unitario
 * @returns {number|null} total, ou null se algum operando não for finito
 */
export const multiplicarDinheiro = (quantidade, unitario) => {
  if (!DECIMAL_SIMPLES.test(String(quantidade))) return null;
  if (!DECIMAL_SIMPLES.test(String(unitario))) return null;

  const a = escalar(quantidade);
  const b = escalar(unitario);
  if (!Number.isSafeInteger(a.digitos * b.digitos)) return null;

  return emCentavos(a.digitos * b.digitos, a.casas + b.casas) / 100;
};
